/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { get } from 'js/lib/user';

import { Button, Dialog, Typography2 } from '@coursera/cds-core';
import { Loading } from '@coursera/coursera-ui';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import InternationalPhoneNumberInput from 'bundles/expression-of-interest/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import { SECTION_EVENT_MAP } from 'bundles/expression-of-interest/constants';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { updateUserProfile } from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  phone: Phone;
  isPhoneLoading: boolean;
  handleSubmit: () => Promise<void | {}>;
  navigateConfirmation: () => void;
  navigateSelectSubject: () => void;
  handlePhoneChange: (data?: ProfilePhone) => void;
  trackingProductId: string;
  eoiButtonSource?: EOIButtonSource;
};

const styles = {
  phoneInput: css`
    padding-bottom: var(--cds-spacing-300);
  `,
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
};

export const PhoneNumberSection = (props: Props): React.ReactElement => {
  const {
    handleSubmit,
    navigateConfirmation,
    navigateSelectSubject,
    phone,
    isPhoneLoading,
    handlePhoneChange,
    trackingProductId,
    eoiButtonSource,
  } = props;

  const [fieldError, setFieldError] = useState<boolean>(false);
  const { trackSubmitEoi } = useEoiEventing(trackingProductId);
  const phoneNumberRef = useRef<HTMLInputElement>(null);

  const { productType } = useEoiOptimizationsContext();

  const baseTrackingData = {
    eoiSection: SECTION_EVENT_MAP.phoneNumberSection,
    eoiButtonSource,
  };

  const disclaimerText = _t(
    'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities.'
  );
  const titleCopy = _t('Add your phone number');
  const supportTextCopy = _t(
    'Our enrollment representatives may reach out to walk you through program details and provide personalized next steps.'
  );
  const submitButtonCopy = _t('Add phone number');

  const { HeadingGroup, Content, Actions } = Dialog;

  const navigateNext = () => {
    if (productType === ProductTypes.DEGREE_HUB) {
      navigateSelectSubject();
    } else {
      navigateConfirmation();
    }
  };

  const handlePhoneSubmit = () => {
    if (phone.phoneNumber === '') {
      setFieldError(true);
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
    } else if (!fieldError) {
      handleSubmit()
        .then(() => {
          const formattedPhone = phone;
          // user profile expects the phone number to be stripped of its country code
          formattedPhone.phoneNumber = phone.phoneNumber.replace(`+${phone.countryCode}`, '');
          const currentUser = get();
          updateUserProfile({
            userId: currentUser.id,
            externalId: currentUser.external_id,
            fullname: currentUser.fullName,
            phone: formattedPhone,
          });

          navigateNext();
          trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'success' });
        })
        .catch(() => {
          trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'api_failure' });
        });
    } else {
      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'invalid_submission' });
    }
  };

  if (isPhoneLoading) {
    return (
      <Content>
        <Loading />
      </Content>
    );
  }

  return (
    <>
      <HeadingGroup supportText={supportTextCopy}>{titleCopy}</HeadingGroup>
      <Content>
        <div css={styles.phoneInput}>
          <InternationalPhoneNumberInput
            profile={{ phone }}
            isOptional={false}
            isEmptyValid={false}
            customStyles={customFormattedPhoneStyles}
            submissionError={fieldError}
            handleFormChange={handlePhoneChange}
            handleError={(hasError: boolean) => {
              setFieldError(hasError);
            }}
            ref={phoneNumberRef}
          />
        </div>

        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {disclaimerText}
        </Typography2>
      </Content>
      <Actions>
        <Button onClick={handlePhoneSubmit} css={styles.primaryButton}>
          {submitButtonCopy}
        </Button>
      </Actions>
    </>
  );
};

export default PhoneNumberSection;
